import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { AnonymousLayout } from "@/layouts/AnonymousLayout/AnonymousLayout";
import { anonymousRouting } from "@/modules/router/routings/anonymousRouting";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { PageMeta } from "@/modules/theme/components/PageMeta";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { ReactComponent as ConfettiImg } from "./helpers/confetti-image.svg";
import { createChangePasswordSuccessPageStyles } from "./helpers/createChangePasswordSuccessPageStyles";

const translationNamespace = "pages/ChangePasswordSuccessPage";

const TIMEOUT_DURATION = 5000;

type ChangePasswordSuccessPageProps = {
  mode: "setup" | "reset";
};

export const ChangePasswordSuccessPage: React.FC<ChangePasswordSuccessPageProps> = ({
  mode,
}) => {
  const { t } = useTranslation(translationNamespace);
  const theme = useTheme();
  const classes = createChangePasswordSuccessPageStyles(theme);
  const history = useHistory();

  useEffect(() => {
    const timeout = setTimeout(() => {
      history.replace(anonymousRouting.buildUrl("signIn"));
    }, TIMEOUT_DURATION);
    return () => {
      clearTimeout(timeout);
    };
  }, [history]);

  const text =
    mode === "reset"
      ? t("The password has been changed!")
      : t("The password has been set successfully!");

  return (
    <AnonymousLayout>
      <PageMeta title={text} />
      <div className={classes.contentWrapper}>
        <ConfettiImg />
        <Typography variant="heading" className={classes.heading}>
          {text}
        </Typography>
        <Typography variant="contentMedium" className={classes.description}>
          {t("You will be redirected to the log in page in a few seconds.")}
        </Typography>
        <div className={classes.loaderWrapper}>
          <Loader size={30} variant="primary" className={classes.loader} />
          <Typography variant="contentMedium">{t("Redirecting...")}</Typography>
        </div>
      </div>
    </AnonymousLayout>
  );
};
